import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { ClientsService } from './clientsApi';
import { ApiResponse } from '../../../../interfaces/models/models/paginated-response.model';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';

const initialState: any = {
    clientsAreLoading: false,
    clientIsLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchClients = createAsyncThunk<any, IPaginationPayload>(
    'clients/tryToFetchClients',
    async ({ pageNumber, pageSize, filters }) => {
        const result = await ClientsService.tryToFetchClients(pageNumber, pageSize, filters);
        return result?.data;
    },
);

export const tryToFetchSingleClient = createAsyncThunk<any, string>(
    'clients/tryToFetchSingleClient',
    async (id: string) => {
        const result = await ClientsService.tryToFetchSingleClient(id);
        return result?.data?.data;
    },
);

export const tryToAddClient = createAsyncThunk<ApiResponse<any>, any>(
    'clients/tryToAddClient',
    async (data: any, { rejectWithValue }) => {
        try {
            const result = await ClientsService.tryToAddClient(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToEditClient = createAsyncThunk<ApiResponse<any>, any>(
    'clients/tryToEditClient',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const result = await ClientsService.tryToEditClient(id, data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToDeleteClient = createAsyncThunk<ApiResponse<any>, string>(
    'clients/tryToDeleteClient',
    async (id, { rejectWithValue }) => {
        try {
            const result = await ClientsService.tryToDeleteClient(id);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const clientsSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchClients
            .addCase(tryToFetchClients.pending, (state) => {
                state.clientsAreLoading = true;
            })
            .addCase(tryToFetchClients.rejected, (state) => {
                state.clientsAreLoading = false;
            })
            .addCase(tryToFetchClients.fulfilled, (state, action) => {
                state.clientsAreLoading = false;
                state.clients = action.payload;
            })

            // tryToFetchSingleClient
            .addCase(tryToFetchSingleClient.pending, (state) => {
                state.clientIsLoading = true;
            })
            .addCase(tryToFetchSingleClient.rejected, (state) => {
                state.clientIsLoading = false;
                state.clientDetails = undefined;
            })
            .addCase(tryToFetchSingleClient.fulfilled, (state, action) => {
                state.clientIsLoading = false;
                state.clientDetails = action.payload;
            })

            // tryToAddClient
            .addCase(tryToAddClient.pending, (state) => {
                state.clientIsLoading = true;
            })
            .addCase(tryToAddClient.rejected, (state) => {
                state.clientIsLoading = false;
            })
            .addCase(tryToAddClient.fulfilled, (state, action) => {
                state.clientIsLoading = false;
                state.requestStatus = 'success';
                const data = action.payload.data || action.meta.arg;
                if (state.clients?.data) state.clients.data.elements = [data, ...state.clients?.data.elements || []];
            })

            // tryToDeleteClient
            .addCase(tryToDeleteClient.rejected, (state) => {
                state.clientIsLoading = false;
                state.requestStatus = 'error';
            })
            .addCase(tryToDeleteClient.fulfilled, (state, action) => {
                state.clientIsLoading = false;
                state.requestStatus = 'success';
                if (state.clients?.data.elements)
                    state.clients.data.elements =
                        { ...state }.clients?.data.elements.filter((client: any) => client?._id !== action.meta.arg) || [];
            })

            // tryToEditClient
            .addCase(tryToEditClient.pending, (state) => {
                state.clientIsLoading = true;
            })
            .addCase(tryToEditClient.rejected, (state) => {
                state.clientIsLoading = false;
            })
            .addCase(tryToEditClient.fulfilled, (state, action) => {
                state.clientIsLoading = false;
                state.requestStatus = 'success';
                const data = action.payload.data || action.meta.arg?.data;
                if (state.clients?.data.elements)
                    state.clients.data.elements =
                        { ...state }.clients?.data.elements.map((client: any) => {
                            if (client?._id === action.meta?.arg?.id) {
                                return {
                                    ...data || client || {},
                                }
                            } else {
                                return client
                            }
                        }) || [];
            })
    },
});

export default clientsSlice.reducer;